import type { AxiosResponse } from 'axios';

import { apiSso } from '~/configs/api/apiConfig';
import env from '~/configs/env';
import type {
  IChangePasswordParams,
  IEmailConfirmationParams,
  IExpiredUserPasswordParams,
  IExternalAuthResult,
  ILoginParams,
  IRegisterParams,
  IRememberPasswordParams,
} from '~/types/api/Auth';
import type IAuthToken from '~/types/models/auth/AuthToken';

export async function fetchLogin(loginData: ILoginParams): Promise<AxiosResponse<IAuthToken>> {
  const query = `username=${loginData.username}&password=${
    loginData.password
  }&client_id=veiling-online&client_secret=${env.CLIENT_SECRET}&scope=openid profile offline_access&grant_type=password&remember_me=${loginData.rememberMe ?? false}`;

  return apiSso.post('/connect/token', query, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
}

export async function rememberPassword(
  rememberPasswordData: IRememberPasswordParams,
): Promise<AxiosResponse> {
  const query = `email=${rememberPasswordData.username}`;
  return apiSso.put('/password/forgot', query, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
}

export function changePassword(changePasswordData: IChangePasswordParams): Promise<AxiosResponse> {
  const query = `password=${changePasswordData.password}&temporaryToken=${encodeURIComponent(
    changePasswordData.temporaryToken as string,
  )}`;

  return apiSso.put('/password/change', query, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
}

export function refreshToken(refreshToken: string, scope: string): Promise<AxiosResponse> {
  const query = `refresh_token=${refreshToken}&client_id=veiling-online&client_secret=${env.CLIENT_SECRET}&scope=${scope}&grant_type=refresh_token`;
  return apiSso.post('/connect/token', query, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
}

export function confirmRegister(temporaryToken: string): Promise<AxiosResponse> {
  const query = `temporaryToken=${encodeURIComponent(temporaryToken)}`;

  return apiSso.put('/user/confirm-register', query, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
}
export async function registerUser(registerData: IRegisterParams): Promise<AxiosResponse> {
  delete registerData.confirmPassword;

  return apiSso.post('/user', registerData);
}
export function expiredUserPasswordForm(
  expiredUserPasswordData: IExpiredUserPasswordParams,
): Promise<AxiosResponse> {
  delete expiredUserPasswordData.confirmPassword;
  return apiSso.put('/password/expired', expiredUserPasswordData);
}
export async function sendEmailConfirmation(
  emailConfirmationData: IEmailConfirmationParams,
): Promise<AxiosResponse> {
  const query = `email=${emailConfirmationData.email}`;

  return apiSso.put('/user/resend-confirmation/email', query, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
}

export async function fetchExternalSocialToken(
  authCode: string,
): Promise<AxiosResponse<IAuthToken>> {
  const body = new URLSearchParams({
    grant_type: 'external_social',
    external_auth_code: authCode,
    client_id: 'veiling-online',
    client_secret: env.CLIENT_SECRET,
    scope: 'openid profile offline_access',
  });

  return apiSso.post('/connect/token', body.toString(), {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
}

export async function confirmExternalLink(
  token: string,
): Promise<AxiosResponse<IExternalAuthResult>> {
  return apiSso.post('/api/external-auth/confirm-link', { token });
}
